import { faCalendarAlt, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateBulkOrderLineEcds } from '../../../../../features/projectManagement/projectManagementSlice'
import useToastr from '../../../../../hooks/useToastr'
import ProjectService from '../../../../../services/ProjectService'
import { E2EProjectLineECDAdjustmentModel } from '../../../../../shared/models/service-models/E2EProjectLineECDAdjustmentModel'
import ProjectOrderlineStatus from '../../enums/project-orderline-status'
import PmrE2EProjectDetailsOrderlineModel, {
  getProjectLineIsCompletedV2,
} from '../../interfaces/pmr-e2e-project-details-orderline-model'
import PmrE2EProjectModel from '../../interfaces/pmr-e2e-project-model'
import classes from './PmrEcdSingleEditTemplate.module.scss'
import PmrEditableDateInput from '../pmr-editable-date-input/PmrEditableDateInput'
import { format, isSameDay, isValid } from 'date-fns'
import PmrEcdAlignmentConfirmDialog from '../pmr-ecd-alignment-confirm-dialog/PmrEcdAlignmentConfirmDialog'
import {
  CalculateECDAlignmentCheckBehavior,
  CalculateECDAlignmentCheckBehaviorSingle,
} from '../../utils/EcdUtils'
import { PmrBulkEditEcdBaseRequestModel } from '../../../../../shared/models/service-models/PmrBulkEditEcdBaseRequest.model'
import moment from 'moment'

interface PmrEcdSingleEditTemplateProps {
  orderlineDetail: PmrE2EProjectDetailsOrderlineModel
  project: PmrE2EProjectModel
  isOrderLineEcd: boolean
  reloadList: () => void
}

const PmrEcdSingleEditTemplate = (props: PmrEcdSingleEditTemplateProps) => {
  const ecdTooltips = ['Order Line', 'FLEX Project']
  const currentECDText = props.isOrderLineEcd ? ecdTooltips[0] : ecdTooltips[1]
  const otherECDText = props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]
  const [isEditMode, setIsEditMode] = useState<boolean>()
  const [isShowAlignConfirmationDialog, setIsShowAlignConfirmationDialog] = useState<boolean>(false)
  const [newAdjustedECD, setNewAdjustedECD] = useState<Date | null>(null)
  const [currentECD, setCurrentECD] = useState<Date | null>(null)
  const [otherECD, setOtherECD] = useState<Date | null>(null)
  const [ecdAdjustmentDetails, setEcdAdjustmentDetails] =
    useState<E2EProjectLineECDAdjustmentModel[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAlign, setIsLoadingAlign] = useState(false)
  const [inputHasError, setInputHasError] = useState<boolean>(false)
  const { showSuccess, showError, showInfo } = useToastr()
  const dispatch = useDispatch()
  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const [isInlineLoading, setIsInlineLoading] = useState(false)

  const handleECDClick = () => {
    if (!isEditable) return

    setNewAdjustedECD(currentECD)
    setIsEditMode(true)
    setTimeout(() => {
      setIsCalendarVisible(true)
    }, 100)
  }

  const projectBookedDate = new Date(props.orderlineDetail.flexProjectBookedDate)
  const calendarStartDate = new Date(2020, 0, 1)
  const calendarEndDate = new Date(2030, 11, 31)

  const isEditable = props.orderlineDetail.orderLineStatus !== ProjectOrderlineStatus.Completed

  const updateECDProps = useCallback(() => {
    const flexProjectEcdFormatted = props.orderlineDetail.flexProjectECD
      ? moment(props.orderlineDetail.flexProjectECD).format('ll')
      : null
    const currentEcd = props.isOrderLineEcd
      ? props.orderlineDetail.orderLineECD
        ? new Date(props.orderlineDetail.orderLineECD)
        : null
      : flexProjectEcdFormatted
        ? new Date(flexProjectEcdFormatted)
        : null

    const otherECD = props.isOrderLineEcd
      ? flexProjectEcdFormatted
        ? new Date(flexProjectEcdFormatted)
        : null
      : props.orderlineDetail.orderLineECD
        ? new Date(props.orderlineDetail.orderLineECD)
        : null

    setCurrentECD(currentEcd)
    setOtherECD(otherECD)
  }, [props.orderlineDetail, props.isOrderLineEcd])

  useEffect(() => {
    updateECDProps()
  }, [updateECDProps])

  const handleEditDate = (selectedNewDate: Date | null, isInputError: boolean) => {
    setInputHasError(isInputError)

    if (typeof selectedNewDate === 'string' && !isValid(selectedNewDate)) {
      return
    }
    selectedNewDate?.setHours(0, 0, 0)
    setNewAdjustedECD(selectedNewDate)
  }

  const handleOnHide = () => {
    if (inputHasError) {
      setIsEditMode(false)
      return
    }

    if (!newAdjustedECD && !currentECD) {
      setIsEditMode(false)
      return
    }

    if (newAdjustedECD && currentECD && isSameDay(newAdjustedECD, currentECD)) {
      setIsEditMode(false)
      return
    }

    setEcdAdjustmentDetails(undefined)

    const isForceAlign = CalculateECDAlignmentCheckBehaviorSingle(
      props.isOrderLineEcd,
      currentECD,
      props.isOrderLineEcd ? newAdjustedECD : otherECD,
      props.isOrderLineEcd ? otherECD : newAdjustedECD
    )

    if (!isForceAlign) {
      const ecdAlignmentDetails: E2EProjectLineECDAdjustmentModel[] = [
        {
          e2EProjectLineId: props.orderlineDetail.id,
          orderlineDescription: props.orderlineDetail.orderLineNumber!,
          currentECD: otherECD,
          adjustedECD: newAdjustedECD!,
          e2EProjectName: props.project.name,
        },
      ]
      setEcdAdjustmentDetails(ecdAlignmentDetails)
    }
    if (!newAdjustedECD && !otherECD) {
      setIsInlineLoading(true)
      handleSubmit(false, true)
    } else if (newAdjustedECD && otherECD && isSameDay(newAdjustedECD, otherECD)) {
      setIsInlineLoading(true)
      handleSubmit(false, false)
    } else setIsShowAlignConfirmationDialog(true)
  }

  const handleClose = () => {
    const isForceAlign = CalculateECDAlignmentCheckBehaviorSingle(
      props.isOrderLineEcd,
      currentECD,
      props.isOrderLineEcd ? newAdjustedECD : otherECD,
      props.isOrderLineEcd ? otherECD : newAdjustedECD
    )

    if (isForceAlign) {
      const isLaterText = props.isOrderLineEcd ? 'later' : 'earlier'
      const isLaterTextHelper = props.isOrderLineEcd ? 'an earlier' : 'a later'
      showInfo(
        'Update Not Allowed',
        `The new ${currentECDText} ECD <strong>${props.orderlineDetail.orderLineNumber}</strong> cannot be ${isLaterText} than the ${otherECDText} ECD. 
          Please update the ${otherECDText} ECD or choose ${isLaterTextHelper} date.`
      )
    } else {
      showInfo(
        'ECD Update Cancelled',
        'No changes were made to the Order Line ECD nor FLEX Project ECD.'
      )
    }

    setIsShowAlignConfirmationDialog(false)
    setIsLoading(false)
    setIsLoadingAlign(false)
    setIsInlineLoading(false)
    setIsEditMode(false)
  }

  const handleSubmit = async (isAlign: boolean, isForceRecalibrate: boolean) => {
    if (isAlign) setIsLoadingAlign(true)
    else setIsLoading(true)

    const payload: PmrBulkEditEcdBaseRequestModel[] = [
      {
        e2eProjectLineId: props.orderlineDetail.id,
        newEcd: newAdjustedECD ? format(new Date(newAdjustedECD), 'yyyy-MM-dd') : null,
        orderLineEcd: props.isOrderLineEcd
          ? null
          : props.orderlineDetail.orderLineECD
            ? format(new Date(props.orderlineDetail.orderLineECD), 'yyyy-MM-dd')
            : null,
        isAlignOrderlineAndFlexProjectEcd: isAlign || isForceRecalibrate,
      },
    ]

    try {
      const service = props.isOrderLineEcd
        ? await ProjectService.bulkUpdateOrderlineEcd(payload)
        : await ProjectService.bulkUpdateFlexProjectEcd(payload)

      if (service.isSuccess) {
        dispatch(updateBulkOrderLineEcds(service.data))

        let toastHeader = `${currentECDText} ECD Updated`
        let message = `The FLEX Project ECD for <strong>${props.orderlineDetail.orderLineNumber}</strong> has been successfully updated.`

        if (props.isOrderLineEcd) {
          message = `The Order Line ECD for <strong>${props.orderlineDetail.orderLineNumber}</strong> has been successfully updated, and the order line schedule has been recalibrated.`
        }

        if (isAlign) {
          if (props.isOrderLineEcd) {
            toastHeader = 'Order Line & FLEX Project ECD Updated'
            message = `The Order Line ECD and FLEX Project ECD for <strong>${props.orderlineDetail.orderLineNumber}</strong> 
            have been successfully updated, and the order line schedule has been recalibrated.`
          } else {
            toastHeader = `FLEX Project & Order Line ECDs Updated`
            message = `The FLEX Project ECD and Order Line ECD for <strong>${props.orderlineDetail.orderLineNumber}</strong> 
            have been successfully updated. The order line schedule has been recalibrated.`
          }
        }

        showSuccess(toastHeader, message)

        props.reloadList()
        setIsShowAlignConfirmationDialog(false)
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred. Please try again later.')
    } finally {
      setIsEditMode(false)
      setIsLoading(false)
      setIsLoadingAlign(false)
      setIsInlineLoading(false)
    }
  }

  return (
    <>
      {isEditMode && (
        <PmrEditableDateInput
          orderBookedDate={projectBookedDate}
          value={newAdjustedECD!}
          onEditDate={handleEditDate}
          yearRange={`${new Date().getFullYear()}:${calendarEndDate.getFullYear()}`}
          onHide={handleOnHide}
          minDate={calendarStartDate}
          maxDate={calendarEndDate}
          visible={isCalendarVisible}
          onVisibleChange={event => setIsCalendarVisible(event.visible)}
          disabled={isInlineLoading}
        />
      )}

      {!isEditMode && (
        <div
          className={clsx({
            'gmawb-clickable-ui': isEditable && currentECD,
          })}
          style={{ display: 'flex' }}
        >
          <div className={classes['calendar-container']} onClick={handleECDClick}>
            <FontAwesomeIcon
              className={clsx(classes['calendar-icon'], {
                'gmawb-clickable-ui': isEditable && !currentECD,
              })}
              icon={faCalendarAlt}
              title="Click to select a date or type in the format ‘'yyyy/mm/dd'’"
            />
          </div>
          <button
            title={!isEditable ? 'ECD of a Completed Order Line cannot be updated/edited' : ''}
            className={clsx(
              classes['ecd'],
              !isEditable && classes['not-allowed'],
              getProjectLineIsCompletedV2(props.orderlineDetail.orderLineStatus) &&
                classes['project-line-is-completed']
            )}
            onClick={handleECDClick}
          >
            {currentECD ? format(currentECD, 'dd MMM yyyy') : undefined}
          </button>
        </div>
      )}
      {isShowAlignConfirmationDialog && (
        <PmrEcdAlignmentConfirmDialog
          isLoading={isLoading}
          isLoadingAlign={isLoadingAlign}
          isVisible={isShowAlignConfirmationDialog}
          isOrderLineEcd={props.isOrderLineEcd}
          ecdAlignmentDetails={ecdAdjustmentDetails}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
      {isInlineLoading && (
        <FontAwesomeIcon style={{ marginLeft: '0.5rem' }} icon={faSpinner} spin={true} />
      )}
    </>
  )
}

export default PmrEcdSingleEditTemplate
